import { createTheme } from '@mui/material/styles'
import { COLOR } from './constants/color.constants'

const defaultTheme = createTheme()

export const theme = createTheme({
    palette: {
        primary: {
            main: COLOR.PRIMARY_MAIN,
            dark: COLOR.PRIMARY_DARK,
            light: COLOR.PRIMARY_LIGHT,
            // contrastText: COLOR.PRIMARY_CONTRAST,
        },
        secondary: {
            main: COLOR.SECONDARY_MAIN,
            dark: COLOR.SECONDARY_DARK,
            light: COLOR.SECONDARY_LIGHT,
            contrastText: COLOR.SECONDARY_CONTRAST,
        },
        error: {
            main: '#E82C1F',
            light: '#FFF5F7',
            dark: '#CA1616',
            // contrastText: '#FFFFFF',
        },
        text: {
            main: '#181B25',
        },
        quote: {
            main: '#9195A1',
            light: '#BDBFC7',
            dark: '#F9FAFB',
        },
    },
    typography: {
        fontFamily: 'Inter',
        button: {
            textTransform: 'none',
        },
        h1: {
            fontSize: '2.5rem',
            fontWeight: 500,
            lineHeight: '3rem',
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 500,
            lineHeight: '2.5rem',
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 500,
            lineHeight: '2rem',
        },
        h4: {
            fontSize: '18px',
            fontWeight: 500,
            lineHeight: '24px',
            [defaultTheme.breakpoints.down('sm')]: {
                fontSize: '18px',
            },
        },
        h5: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: '1.5rem',
            color: '#121317',
        },
        h6: {
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: '1.25rem',
        },
        body1: {
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
        },
        body2: {
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: '1.25rem',
        },
        caption1: {
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: '1rem',
        },
        caption2: {
            fontSize: '0.75rem',
            fontWeight: 500,
            lineHeight: '1rem',
        },
        caption3: {
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: '1rem',
        },
        caption4: {
            fontSize: '10px',
            fontWeight: 400,
            lineHeight: '12px',
        },
        Header3: {
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '28px',
            verticalAlign: 'middle',
        },
        Subtitle1: {
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            verticalAlign: 'middle',
        },
        Description: {
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16px',
            verticalAlign: 'middle',
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: () => ({
                'body ::-webkit-scrollbar': {
                    width: '6px',
                    height: '6px',
                },

                'body ::-webkit-scrollbar-track': {
                    background: '#F4F4F6;',
                },

                'body ::-webkit-scrollbar-thumb': {
                    borderRadius: '100px',
                    backgroundColor: '#DEDFE3',
                },

                'body ::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#DEDFE3',
                },

                'body ::-webkit-scrollbar-thumb:active': {
                    backgroundColor: '#DEDFE3',
                },
            }),
        },
        MuiTypography: {
            defaultProps: {
                color: COLOR.SECONDARY_CONTRAST,
                variantMapping: {
                    caption1: 'p',
                    caption3: 'span',
                },
            },
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    color: '#006CFD',
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    color: '#FFFFFF',
                    // Some CSS
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '100px',
                    gap: '8px',
                    display: 'flex',
                    padding: '10px 24px',
                    height: '40px',
                },
                containedPrimary: {
                    color: COLOR.PRIMARY_CONTRAST,
                    backgroundColor: COLOR.PRIMARY_MAIN,
                    boxShadow: 'none',
                    '&:active': {
                        color: COLOR.PRIMARY_CONTRAST,
                        backgroundColor: COLOR.PRIMARY_DARK,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: COLOR.DISABLED_BG,
                        color: COLOR.DISABLED_COLOR,
                    },
                    '&.MuiButton-loading': {
                        backgroundColor: COLOR.BUTTON_LOADING_PRIMARY_COLOR,
                        color: COLOR.BUTTON_LOADING_PRIMARY_COLOR,
                        cursor: 'wait',
                        pointerEvents: 'none',
                    },
                },
                containedSecondary: {
                    border: '1px solid #E7E8EE',
                    color: COLOR.SECONDARY_CONTRAST,
                    backgroundColor: 'rgb(249, 250, 251)',
                    boxShadow: 'none',
                    '&:active': {
                        color: COLOR.SECONDARY_CONTRAST,
                        backgroundColor: COLOR.PRIMARY_LIGHT,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: COLOR.DISABLED_BG,
                        color: COLOR.DISABLED_COLOR,
                    },
                    '&.MuiButton-loading': {
                        backgroundColor: COLOR.BUTTON_LOADING_PRIMARY_COLOR,
                        color: COLOR.BUTTON_LOADING_SECONDARY_COLOR,
                        cursor: 'wait',
                        pointerEvents: 'none',
                    },
                },
                outlinedPrimary: {
                    boxShadow: 'none',
                    '&:active': {
                        backgroundColor: COLOR.PRIMARY_LIGHT,
                        color: COLOR.PRIMARY_LINK,
                    },
                    '&.Mui-disabled': {
                        color: COLOR.DISABLED_COLOR,
                    },
                    '&.MuiButton-loading': {
                        color: COLOR.PRIMARY_LINK,
                        cursor: 'wait',
                        pointerEvents: 'none',
                    },
                },
            },
            variants: [
                {
                    props: { variant: 'text', color: 'primary' },
                    style: {
                        color: '#006CFD',
                        borderRadius: '100px',
                        '&:hover': {
                            background: '#F9FAFB',
                        },
                        '&:active': {
                            background: '#F4F4F6',
                        },
                    },
                },
                {
                    props: { variant: 'text', color: 'secondary' },
                    style: {
                        color: '#121317',
                        // border: ' 1px solid #E9EAED',
                        '&:hover': {
                            background: '#F9FAFB',
                            // border: ' 1px solid #006CFD',
                        },
                        '&:active': {
                            background: '#F4F4F6',
                            // border: '1px solid transparent',
                        },
                    },
                },
                // {
                //     props: { variant: 'contained', color: 'secondary' },
                //     style: {
                //         background: '#F9FAFB',
                //         color: '#121317',
                //         // border: ' 1px solid #E9EAED',
                //         '&:hover': {
                //             background: '#F9FAFB',
                //             // border: ' 1px solid #006CFD',
                //         },
                //         '&:active': {
                //             background: '#F4F4F6',
                //             // border: '1px solid transparent',
                //         },
                //     },
                // },
                {
                    props: { variant: 'outlined', color: 'primary' },
                    style: {
                        backgroundColor: '#FFF',
                        color: '#121317',
                        border: ' 1px solid #E9EAED',
                        '&:hover': {
                            background: '#F9FAFB',
                            border: ' 1px solid #E9EAED',
                        },
                        '&:active': {
                            background: '#F4F4F6',
                            border: '1px solid #E9EAED',
                        },
                    },
                },
                {
                    props: { variant: 'outlined', color: 'error' },
                    style: {
                        border: 'none',
                        color: '#E82C1F',
                        background: '#F9FAFB',
                        '&:hover': {
                            background: '#F4F4F6',
                        },
                        '&:active': {
                            background: '#F4F4F6',
                        },
                    },
                },
            ],
        },

        MuiLink: {
            styleOverrides: {
                root: {
                    color: '#006CFD',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: '#121317',
                },
            },
            variants: [
                {
                    props: { variant: 'text', color: 'primary' },
                    style: {
                        border: '1px solid transparent',
                        '&:hover': {
                            background: '#F9FAFB',
                            border: ' 1px solid #006CFD',
                        },
                        '&:active': {
                            background: '#F4F4F6',
                            border: '1px solid transparent',
                        },
                    },
                },
                {
                    props: { variant: 'contained', color: 'primary' },
                    style: {
                        background: '#006CFD',
                        '&:hover': {
                            background: '#004B9F',
                        },
                        '&:active': {
                            background: '#004B9F',
                        },
                    },
                },
                {
                    props: { variant: 'outlined', color: 'primary' },
                    style: {
                        color: '#121317',
                        border: ' 1px solid #E9EAED',
                        '&:hover': {
                            background: '#F9FAFB',
                            // border: ' 1px solid #006CFD',
                        },
                        '&:active': {
                            background: '#F4F4F6',
                            // border: '1px solid transparent',
                        },
                    },
                },

                {
                    props: { variant: 'contained', color: 'error' },
                    style: {
                        color: '#E82C1F',
                        // border: ' 1px solid #E72323',
                        '&:hover': {
                            background: '#EF4848',
                            // border: ' 1px solid #EF4848',
                        },
                        '&:active': {
                            background: '#EF4848',
                            color: '#fff',
                            // border: '1px solid #CA1616',
                        },
                    },
                },

                {
                    props: { size: 'large' },
                    style: {
                        width: 40,
                        height: 40,
                        padding: 10,
                    },
                },
                {
                    props: { size: 'medium' },
                    style: {
                        width: 36,
                        height: 36,
                        padding: 8,
                    },
                },
            ],
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    background: '#fff',
                    // borderRadius: '28px',
                    // padding: '18px 16px'
                    boxShadow: 'none',
                    // margin: 2,
                },
            },
        },
        MuiToolbar: {
            defaultProps: {},
            styleOverrides: {
                root: {
                    background: '#fff',
                    borderRadius: '28px',
                    // padding: '18px 16px',
                    boxShadow: 'none',
                    // margin: 2,
                },
            },
        },

        MuiTextField: {
            defaultProps: {
                InputLabelProps: {
                    shrink: true,
                },
            },

            styleOverrides: {
                root: {
                    // padding: '10px 16px',
                    // background: '#fff',
                    // borderRadius: '40px',
                    // padding: '18px 16px',
                    // boxShadow: '0px 8px 16px 0px rgba(0, 0, 0, 0.08)',
                    // margin: 2,
                    // border: 'none',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    // background: '#fff',
                    borderRadius: '28px',
                    padding: '16px 8px',
                    boxShadow: 'none',
                    m: 0,
                    // margin: 2,
                    border: 'none',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: '#F9FAFB',
                        borderRadius: '11px',
                    },
                    '&.Mui-selected': {
                        backgroundColor: '#F4F4F6',
                    },
                    // background: '#fff',
                    borderRadius: '11px',
                    // padding: '16px',
                    // boxShadow: 'none',
                    // m: 0,
                    // margin: 2,
                    // border: 'none',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                root: {
                    '& .MuiMenu-paper': {
                        borderRadius: '8px',
                        boxShadow: '0px 4px 19.2px 0px rgba(0, 0, 0, 0.10)',
                    },
                    '& .MuiListItemText-root': {
                        paddingRight: '16px',
                        fontWeight: 500,
                    },
                    '& .MuiListItemText-root, & .MuiSvgIcon-root': {
                        // color: '#9195A1',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: '28px',
                    // padding: '32px 24px 16px',
                    boxShadow: 'none',
                    // m: 0,

                    // margin: 2,
                    // border: 'none',
                    '&.Paper-primary': {
                        background: '#F7F8F9',
                        padding: '16px',
                        borderRadius: '17px',
                        margin: '0',
                    },
                },
            },
        },

        MuiTooltip: {
            defaultProps: {
                enterTouchDelay: 0
            },
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#322F35',
                    fontSize: '14px',
                    lineHeight: '20px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    boxShadow: '0px 4px 4px 0px #0000000D',
                },
            },
        },

        MuiAccordion: {
            styleOverrides: {
                root: {
                    background: '#fff',
                    boxShadow: 'none',
                    padding: 0,
                    margin: 0,
                    border: 'none',
                    '&::before': {
                        display: 'none',
                    },
                },
            },
        },

        MuiTableHead: {
            styleOverrides: {
                root: {
                    borderTop: '1px solid #F4F4F6',
                },
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    padding: '24px',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    color: '#121317',
                    padding: '8px 16px',
                    [defaultTheme.breakpoints.down('sm')]: {
                        fontSize: '12px',
                    },
                },
            },
        },
        MuiSelect: {
            InputProps: {
                disableUnderline: true,
            },
            styleOverrides: {
                root: {
                    border: 'none',
                    boxShadow: 'none',
                },
                select: {
                    padding: '12px 16px',
                    border: 'none',
                    color: '#121317',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {},
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    '&>label': {
                        color: '#181B25',
                        fontWeight: 500,
                        top: '-12px',
                        left: '-12px',
                    },
                },
            },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '100px',
                    boxShadow: 'none',
                    color: '#5A5F72',
                    '&.MuiInputBase-colorPrimary': {
                        border: '1px solid #E9EAED',
                        background: 'none',
                    },
                    '&.MuiInputBase-colorSecondary': {
                        background: '#F9FAFB',
                    },
                    '& >, input': {
                        padding: '10px 16px',
                    },
                    '&  .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '&>input': {
                        height: '20px',
                        padding: '10px 16px',
                    },
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    // height: '20px',
                    border: '1px solid #E9EAED',
                    borderRadius: '100px',
                    boxShadow: 'none',
                    // background: '#FFF',
                    color: '#121317',
                    // borderColor: '#5E626E',
                    // padding: '10px 16px',
                    // top: '-4px',
                    // left: '8px',
                    '& > input': {
                        padding: '10px 16px',
                        color: 'red',
                        '&::before': {
                            // border: '3px solid red',
                        },
                        '&::after': {
                            // border: '3px solid red',
                        },
                    },
                    '&  .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                },
            },
        },
        MuiChip: {
            variants: [
                {
                    props: { color: 'primary' },
                    style: {
                        color: '#000',
                        padding: 2,
                        border: '1px solid #004B9F',
                        background: COLOR.PRIMARY_LIGHT,
                    },
                },
            ],
            styleOverrides: {
                root: {
                    '& > .MuiChip-deleteIcon': {
                        color: '#000',
                        '&:hover': {
                            color: '#000',
                        },
                    },

                    '& > .MuiChip-label': {
                        // padding: 0,
                    },
                    '& > .MuiChip-icon': {
                        margin: '8px',
                    },
                    '& > .MuiChip-labelSmall': {
                        marginRight: '8px',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    padding: '16px 0',
                    borderBottom: '1px solid #DEDFE3',
                    '& .MuiTabs-list': {
                        gap: '8px',
                    },
                },
                indicator: {
                    display: 'none',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    minHeight: 'auto',
                    backgroundColor: '#F4F4F6',
                    color: '#5E626E',
                    borderRadius: ' 100px',
                    // background: 'none',
                    minWidth: '44px',
                    padding: '8px 12px',
                    '&.Mui-selected': {
                        // padding: '8px 12px',
                        borderRadius: '100px',
                        background: '#004B9F',
                        color: '#ffffff',
                    },
                    [defaultTheme.breakpoints.down('sm')]: {
                        fontSize: '12px',
                    },
                },
            },
            variants: [
                {
                    props: { variant: 'secondary' },
                    style: {
                        backgroundColor: '#ffffff',
                        '&.Mui-selected': {
                            borderRadius: '100px',
                            background: '#F4F4F6',
                            color: '#121317',
                            border: '1px solid #E9EAED',
                        },
                    },
                },
            ],
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    background: '#F4F4F6',
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: '#006CFD',
                    },
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    color: '#004B9F',
                },
            },
        },

        MuiDialog: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        borderRadius: '11px',
                    },
                },
                paper: {
                    [defaultTheme.breakpoints.down('sm')]: {
                        margin: '16px',
                    },
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '1.125rem',
                    fontWeight: 500,
                    lineHeight: '1.75rem',
                    borderBottom: '1px solid #F4F4F6',
                    padding: '20px 16px 20px 24px',
                    marginBottom: '32px',
                    '& + .MuiDialogContent-root': {
                        paddingTop: '24px !important',
                    },
                    [defaultTheme.breakpoints.down('sm')]: {
                        padding: '16px',
                    },
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: '32px 24px 28px 24px  ',
                    [defaultTheme.breakpoints.down('sm')]: {
                        padding: '16px',
                    },
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '16px  32px 32px 32px',
                    // justifyContent: 'center',
                    '&  .MuiButton-root': {
                        // width: '100%',
                    },
                },
            },
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: {
                    color: '#000',
                    fontSize: '1rem',
                    fontWeight: 400,
                    lineHeight: '1.5rem',
                },
            },
        },

        MuiSnackbar: {
            styleOverrides: {
                MuiSnackbarContent: {
                    // borderRadius: '20px',
                },
                root: {
                    borderRadius: '20px',
                    '&.snackbar-error .MuiPaper-root, &.snackbar-success .MuiPaper-root': {
                        boxShadow:
                            '0px 4px 8px 3px rgba(0, 0, 0, 0.15), 0px 1px 20px 0px rgba(0, 0, 0, 0.05)',

                        borderRadius: '20px',
                    },
                    '&.snackbar-error .MuiPaper-root': {
                        background: '#FDD8DC',
                    },
                    '&.snackbar-success .MuiPaper-root': {
                        background: '#F9FEEB',
                    },
                    '.MuiSnackbarContent-root': {
                        padding: '0',
                    },
                    '.MuiSnackbarContent-message': {
                        color: '#181B25',
                        fontSize: '14px',
                        padding: '14px 16px',
                        margin: '0 auto ',
                    },
                    bottom: '16px',
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    width: 32,
                    height: 20,
                    padding: 0,
                    '& .MuiSwitch-switchBase': {
                        padding: 0,
                        margin: '5px',
                        transitionDuration: '300ms',
                        color: '#BDBFC7',
                        '&.Mui-checked': {
                            transform: 'translateX(16px)',
                            color: '#fff',
                            margin: '2px -1px',
                            '& + .MuiSwitch-track': {
                                backgroundColor: '#006CFD',
                                opacity: 1,
                                border: 0,
                            },
                            '&.Mui-disabled + .MuiSwitch-track': {
                                opacity: 0.5,
                                color: '#BDBFC7',
                            },
                            '.MuiSwitch-thumb': {
                                width: '15px',
                                height: '15px',
                            },
                        },
                        '&.Mui-focusVisible .MuiSwitch-thumb': {
                            color: '#33cf4d',
                            border: '6px solid #fff',
                        },
                        '&.Mui-disabled .MuiSwitch-thumb': {
                            color: '#ccc',
                        },
                        '&.Mui-disabled + .MuiSwitch-track': {
                            color: '#BDBFC7',
                            opacity: 0.3,
                        },
                    },
                    '& .MuiSwitch-thumb': {
                        boxSizing: 'border-box',
                        width: 10,
                        height: 10,
                        boxShadow: 'none',
                    },
                    '& .MuiSwitch-track': {
                        borderRadius: 26 / 2,
                        backgroundColor: '#F9F9FA',
                        opacity: 1,
                        border: '1.25px solid #BDBFC7',
                    },
                },
            },
        },
        MuiPickersCalendarHeader: {
            styleOverrides: {
                root: {
                    width: '268px',
                    padding: ' 0 20px 0 36px',
                },
            },
        },
        MuiYearCalendar: {
            styleOverrides: {
                root: {
                    width: '268px',
                },
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    width: '34px',
                    height: '34px',
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: '#E3E3E8',
                    '&.Mui-checked': {
                        color: '#004B9F',
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: '#E3E3E8',
                    '&.Mui-checked': {
                        color: '#004B9F',
                    },
                },
            },
        },
    },
})
